import { useContext } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import Button from '@/design_system/Button';
import { InputSelect } from '@/design_system/InputSelect/InputSelect';
import {
  Cell,
  Column,
  Header,
  InputMoneyCell,
  InputNumberCell,
  InputPercentageCell,
  Row,
} from '@/design_system/Table/Table';
import IconTrash from '@/icons/Trash.svg';
import { AgreementFromGetById } from '@/models/agreements';
import { formatCurrency, formatPercentage } from '@/utils/number';

import { AgreementEditContext } from '../AgreementEditContext';

export const getPriceAgreementColumnWidths = ({
  withMargin = false,
  withWorkshopWarrantyDurationInMonths = false,
  withActions = false,
}: {
  withMargin?: boolean;
  withWorkshopWarrantyDurationInMonths?: boolean;
  withActions?: boolean;
}) => {
  const widths = ['3fr', '1fr', '1fr', '1fr']; // name, amount w/o tax, tax rate, amount

  if (withMargin) {
    widths.push('1fr');
  }

  if (withWorkshopWarrantyDurationInMonths) {
    widths.push('1fr');
  }

  if (withActions) {
    widths.push('auto');
  }

  return widths;
};

export const PriceAgreementTableHeader = ({
  withActions = false,
  withMargin = false,
  withWorkshopWarrantyDurationInMonths = false,
}: {
  withActions?: boolean;
  withMargin?: boolean;
  withWorkshopWarrantyDurationInMonths?: boolean;
}) => {
  const { t } = useLingui();

  return (
    <Header>
      <Row>
        <Column>
          <Trans id="settings.organization.entities.agreements.detail.table.name">Name</Trans>
        </Column>
        <Column>
          <Trans id="settings.organization.entities.agreements.detail.table.amount-before-taxes">
            Amount w/o tax
          </Trans>
        </Column>
        <Column>
          <Trans id="settings.organization.entities.agreements.detail.table.tax-rate">
            Tax rate
          </Trans>
        </Column>
        <Column>
          <Trans id="settings.organization.entities.agreements.detail.table.amount">Amount</Trans>
        </Column>
        {withMargin && (
          <Column>
            <Trans id="settings.organization.entities.agreements.detail.table.margin">Margin</Trans>
          </Column>
        )}
        {withWorkshopWarrantyDurationInMonths && (
          <Column>
            <Trans id="settings.organization.entities.agreements.detail.table.workshop-warranty-duration-in-months">
              Workshop warranty duration in months
            </Trans>
          </Column>
        )}
        {withActions && (
          <Column
            ariaLabel={t({
              id: 'settings.organization.entities.agreements.detail.table.table.actions',
              message: 'Actions',
            })}
          />
        )}
      </Row>
    </Header>
  );
};

export const PriceAgreementTableRow = ({
  price,
  children,
  withMargin = false,
  withWorkshopWarrantyDurationInMonths = false,
}: {
  price: AgreementFromGetById['prices'][number];
  children: React.ReactNode;
  withMargin?: boolean;
  withWorkshopWarrantyDurationInMonths?: boolean;
}) => {
  return (
    <Row>
      <Cell>{children}</Cell>
      <Cell>{formatCurrency(price.amountBeforeTaxes, price.currency)}</Cell>
      <Cell>
        {price.taxes.map((tax) => formatPercentage(tax.rate)).join(', ') || formatPercentage(0)}
      </Cell>
      <Cell>{formatCurrency(price.amount, price.currency)}</Cell>
      {withMargin && <Cell>{formatPercentage(price.margin)}</Cell>}
      {withWorkshopWarrantyDurationInMonths && (
        <Cell>{price.workshopWarrantyDurationInMonths ?? '-'}</Cell>
      )}
    </Row>
  );
};

export const PriceAgreementTableRowEdit = ({
  price,
  children,
  withMargin = false,
  withWorkshopWarrantyDurationInMonths = false,
}: {
  price: AgreementFromGetById['prices'][number];
  children: React.ReactNode;
  withMargin?: boolean;
  withWorkshopWarrantyDurationInMonths?: boolean;
}) => {
  const { t } = useLingui();

  const { draft, updateDraft, updatePrice, isUpdateAgreementPending } =
    useContext(AgreementEditContext);

  return (
    <Row key={price.id}>
      <Cell>{children}</Cell>
      <InputMoneyCell
        value={price.amountBeforeTaxes ?? undefined}
        currency={price.currency}
        onChange={(value) => {
          updatePrice(price.id, { amountBeforeTaxes: isNaN(value) ? null : value });
        }}
        ariaLabel={t({
          id: 'settings.organization.entities.agreements.detail.table.row.amount-before-taxes',
          message: 'Amount w/o tax',
        })}
        cellProps={{ isReadonly: draft.amountToConsider === 'amount' }}
      >
        {formatCurrency(price.amountBeforeTaxes, price.currency)}
      </InputMoneyCell>
      <InputPercentageCell
        value={price.taxes[0]?.rate ?? 0}
        onChange={(value) =>
          updatePrice(price.id, {
            taxes: [
              // Amount will get recomputed automatically
              { rate: isNaN(value) ? 0 : value, amount: 0 },
            ],
          })
        }
        ariaLabel={t({
          id: 'settings.organization.entities.agreements.detail.table.row.tax-rate',
          message: 'Tax rate',
        })}
      >
        {formatPercentage(price.taxes[0]?.rate ?? 0)}
      </InputPercentageCell>
      <InputMoneyCell
        value={price.amount ?? undefined}
        currency={price.currency}
        onChange={(value) => updatePrice(price.id, { amount: isNaN(value) ? null : value })}
        ariaLabel={t({
          id: 'settings.organization.entities.agreements.detail.table.row.amount',
          message: 'Amount',
        })}
        cellProps={{ isReadonly: draft.amountToConsider === 'amountBeforeTaxes' }}
      >
        {formatCurrency(price.amount, price.currency)}
      </InputMoneyCell>
      {withMargin && (
        <InputPercentageCell
          value={price.margin ?? undefined}
          onChange={(value) =>
            updateDraft({
              prices: draft.prices.map((p) =>
                p.id === price.id ? { ...p, margin: isNaN(value) ? null : value } : p
              ),
            })
          }
          ariaLabel={t({
            id: 'settings.organization.entities.agreements.detail.table.row.margin',
            message: 'Margin',
          })}
        >
          {formatPercentage(price.margin)}
        </InputPercentageCell>
      )}
      {withWorkshopWarrantyDurationInMonths && (
        <InputNumberCell
          value={price.workshopWarrantyDurationInMonths ?? undefined}
          onChange={(value) =>
            updateDraft({
              prices: draft.prices.map((p) =>
                p.id === price.id
                  ? { ...p, workshopWarrantyDurationInMonths: isNaN(value) ? null : value }
                  : p
              ),
            })
          }
          ariaLabel={t({
            id: 'settings.organization.entities.agreements.detail.table.row.workshop-warranty-duration-in-months',
            message: 'Workshop warranty duration in months',
          })}
        >
          {price.workshopWarrantyDurationInMonths}
        </InputNumberCell>
      )}
      <Cell>
        <Button
          size="small"
          variant="secondary-danger"
          disabled={isUpdateAgreementPending}
          onClick={() => updateDraft({ prices: draft.prices.filter((p) => p.id !== price.id) })}
          iconOnly
        >
          <IconTrash />
        </Button>
      </Cell>
    </Row>
  );
};

export function PriceAgreementTableRowNew<T>({
  placeholder,
  options,
  onSelect,
  withMargin = false,
  withWorkshopWarrantyDurationInMonths = false,
}: {
  placeholder: string;
  options: T[];
  onSelect: (value: T) => Partial<AgreementFromGetById['prices'][number]>;
  withMargin?: boolean;
  withWorkshopWarrantyDurationInMonths?: boolean;
}) {
  const { draft, updateDraft, isUpdateAgreementPending } = useContext(AgreementEditContext);

  const emptyColumnsCount =
    3 + // amount w/o tax, tax rate, amount
    (withMargin ? 1 : 0) +
    (withWorkshopWarrantyDurationInMonths ? 1 : 0) +
    1; // actions

  return (
    <Row>
      <Cell isEditable>
        <InputSelect
          value={null}
          onChange={(value) =>
            updateDraft({
              prices: [
                ...draft.prices,
                {
                  id: crypto.randomUUID(),
                  agreementId: draft.id,
                  actionTypeOrganization: null,
                  actionTypeOrganizationId: null,
                  packActionTypeOrganization: null,
                  packActionTypeOrganizationId: null,
                  sparePartTypeOrganization: null,
                  sparePartTypeOrganizationId: null,
                  feeType: null,
                  feeTypeId: null,
                  currency: draft.currency ?? 'EUR',
                  amount: null,
                  taxes: [
                    {
                      rate: draft.defaultCustomPriceTaxRates[0] ?? 0,
                      amount: 0,
                    },
                  ],
                  amountBeforeTaxes: null,
                  margin: null,
                  workshopWarrantyDurationInMonths: null,
                  ...onSelect(value!),
                },
              ],
            })
          }
          options={options}
          placeholder={placeholder}
          isDisabled={isUpdateAgreementPending}
        />
      </Cell>
      <Cell colSpan={emptyColumnsCount} />
    </Row>
  );
}
